import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { parseCombatTag, isRoundHeader, isRewardLine } from '../../utils/combatLogUtils';
import { formatDateTimeUTC } from '../../utils/dateFormat';
import BattleViewer2D, { BattleData } from './Admin/BattleViewer2D';
import styles from './DeathScreen.module.css';

type BattleLogMode = 'new2d' | 'scrolling' | 'instant';

interface ParticipantSummary {
  combatantId: number;
  name?: string;
  team: string;
  type: string;
  level?: number;
  isBot?: boolean;
  damage: number;
  highestDamageDealt: number;
  damageTaken: number;
  attacksMade: number;
  dodgedAttacks: number;
  parries: number;
  blockedAttacks: number;
  missedAttacks: number;
  criticalHits: number;
  taunts?: number;
}

interface BattleReportData {
  reportData: {
    participantSummaries: { [key: string]: ParticipantSummary };
    teamSummaries: { teamOne: any; teamTwo: any };
    combatLog: string[];
    seasonName: string;
    winner: string;
  };
  title: string;
  createdAt: string;
  guildInfo: { [mercenaryId: string]: { guildId: number | null; guildName: string | null; guildTag: string | null } };
}

const DeathScreen: React.FC = () => {
  const { battleReportId } = useParams<{ battleReportId: string }>();
  const [battleReport, setBattleReport] = useState<BattleReportData | null>(null);
  const [error, setError] = useState<boolean>(false);
  const [battleLogMode, setBattleLogMode] = useState<BattleLogMode>('new2d');
  const [battleViewSpeed, setBattleViewSpeed] = useState(1);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [viewingComplete, setViewingComplete] = useState(false);
  const [revealedCount, setRevealedCount] = useState(0);
  const [playerTeam, setPlayerTeam] = useState<string>('');
  const [playerGuildId, setPlayerGuildId] = useState<number | null>(null);
  const scrollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const playerMercenaryId = parseInt(localStorage.getItem('mercenaryId') || '0');

  // Fetch user settings for battle log mode preference
  useEffect(() => {
    axios.get('/api/user/settings', { withCredentials: true })
      .then(res => {
        if (res.data.battleLogMode) setBattleLogMode(res.data.battleLogMode as BattleLogMode);
        if (res.data.battleViewSpeed) setBattleViewSpeed(Number(res.data.battleViewSpeed));
      })
      .catch(() => { /* use default 'new2d' */ })
      .finally(() => setSettingsLoaded(true));
  }, []);

  useEffect(() => {
    const fetchBattleReport = async () => {
      try {
        const response = await axios.get(`/api/battle/result/${battleReportId}`, { withCredentials: true });
        if (response.data && response.data.reportData && response.data.reportData.combatLog) {
          const data: BattleReportData = response.data;
          setBattleReport(data);
          const playerSummary = Object.values(data.reportData.participantSummaries).find(
            (p) => p.combatantId === playerMercenaryId
          );
          if (playerSummary) {
            setPlayerTeam(playerSummary.team);
          }
          if (data.guildInfo && data.guildInfo[playerMercenaryId]) {
            setPlayerGuildId(data.guildInfo[playerMercenaryId].guildId);
          }
        } else {
          setError(true);
        }
      } catch (err) {
        console.error('Error fetching battle report:', err);
        setError(true);
      }
    };
    fetchBattleReport();
  }, [battleReportId]);

  const handleViewingComplete = useCallback(() => {
    setViewingComplete(true);
  }, []);

  // Scrolling mode interval
  useEffect(() => {
    if (battleLogMode !== 'scrolling' || !battleReport || viewingComplete) return;
    const combatData = battleReport.reportData.combatLog;
    if (!combatData || combatData.length === 0) { handleViewingComplete(); return; }
    if (revealedCount >= combatData.length) { handleViewingComplete(); return; }

    scrollIntervalRef.current = setInterval(() => {
      setRevealedCount(prev => {
        const next = prev + 1;
        if (next >= combatData.length) {
          if (scrollIntervalRef.current) { clearInterval(scrollIntervalRef.current); scrollIntervalRef.current = null; }
          handleViewingComplete();
        }
        return next;
      });
    }, Math.max(50, Math.round(550 / battleViewSpeed)));

    return () => {
      if (scrollIntervalRef.current) { clearInterval(scrollIntervalRef.current); scrollIntervalRef.current = null; }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [battleLogMode, battleReport, battleViewSpeed]);

  const skipScrolling = useCallback(() => {
    if (scrollIntervalRef.current) { clearInterval(scrollIntervalRef.current); scrollIntervalRef.current = null; }
    if (battleReport) setRevealedCount(battleReport.reportData.combatLog.length);
    handleViewingComplete();
  }, [battleReport, handleViewingComplete]);

  const handleCreateNewMercenary = async () => {
    try {
      await axios.post('/api/user/reset-death-status', {}, { withCredentials: true });
      window.location.href = '/create-mercenary';
    } catch (err) {
      console.error('Error updating user death status:', err);
    }
  };

  const handleSwitchMercenary = async () => {
    try {
      await axios.post('/api/user/reset-death-status', {}, { withCredentials: true });
      window.location.href = '/mercenaries';
    } catch (err) {
      console.error('Error updating user death status:', err);
    }
  };

  const getParticipantClass = (id: number) => {
    if (!battleReport) return 'enemyTeam1';
    const participant = Object.values(battleReport.reportData.participantSummaries).find(
      (p) => p.combatantId === id
    );
    if (!participant) return 'enemyTeam1';
    if (id === playerMercenaryId && participant.type === 'mercenary') return 'playerMerc';
    if (participant.team === playerTeam) return 'playerTeam';
    const allParticipants = Object.values(battleReport.reportData.participantSummaries) as ParticipantSummary[];
    const enemyTeams = [...new Set(allParticipants.filter((p) => p.team !== playerTeam).map((p) => p.team))];
    const teamIndex = enemyTeams.indexOf(participant.team);
    if (teamIndex === 0) return 'enemyTeam1';
    if (teamIndex === 1) return 'enemyTeam2';
    if (teamIndex === 2) return 'enemyTeam3';
    return 'enemyTeam1';
  };

  const renderTeamPreview = (teamType: string, participantSummaries: any) => {
    if (!participantSummaries) return null;
    const teamMembers = Object.values(participantSummaries).filter(
      (participant: any) => participant.team === teamType
    );
    return (
      <div className={styles.teamPreview}>
        <h4>{teamType === 'teamOne' ? 'Team One' : 'Team Two'}</h4>
        <ul>
          {teamMembers.map((participant: any, index: number) => {
            const isGuildMate = battleReport?.guildInfo && playerGuildId &&
              battleReport.guildInfo[participant.combatantId]?.guildId === playerGuildId &&
              participant.combatantId !== playerMercenaryId;
            const colorClass = getParticipantClass(participant.combatantId);
            const classToApply = (styles as any)[colorClass] || styles.participant;
            return (
              <li key={index} className={classToApply}>
                {participant.isBot && <span className={styles.botBadge}>B</span>}
                {isGuildMate && <span className={styles.guildCrown}>♔</span>}
                {participant.name} (Level {participant.level || 'N/A'})
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const parseLogWithColors = (log: string) => {
    const { text } = parseCombatTag(log);
    return text.split(/(\{\{.*?\}\})/).map((segment, i) => {
      const match = segment.match(/\{\{(.*?)\}\}/);
      if (match) {
        const parts = match[1].split(':');
        if (parts.length === 3) {
          const [, id, name] = parts;
          const combatantId = parseInt(id);
          const colorClass = getParticipantClass(combatantId);
          const isGuildMate = battleReport?.guildInfo && playerGuildId &&
            battleReport.guildInfo[combatantId]?.guildId === playerGuildId &&
            combatantId !== playerMercenaryId;
          const botSummary = battleReport?.reportData?.participantSummaries &&
            Object.values(battleReport.reportData.participantSummaries).find((p) => p.combatantId === combatantId);
          const isBot = botSummary?.isBot;
          const classToApply = (styles as any)[colorClass] || styles.participant;
          return (
            <span key={i} className={classToApply}>
              {isBot && <span className={styles.botBadge}>B</span>}
              {isGuildMate && <span className={styles.guildCrown}>♔</span>}
              {name}
            </span>
          );
        }
        return <span key={i} className={styles.enemyTeam1}>{parts[parts.length - 1]}</span>;
      }
      return <span key={i}>{segment}</span>;
    });
  };

  const parseRewardLine = (line: string): React.ReactNode => {
    const { text: cleanLine } = parseCombatTag(line);
    const receivesIdx = cleanLine.indexOf(' receives ');
    if (receivesIdx === -1) return <>{cleanLine}</>;
    const name = cleanLine.substring(0, receivesIdx);
    const rest = cleanLine.substring(receivesIdx + ' receives '.length);
    const byDefeatingIdx = rest.indexOf(' by defeating ');
    const mainPart = byDefeatingIdx !== -1 ? rest.substring(0, byDefeatingIdx) : rest.replace(/\.$/, '');
    const beastsPart = byDefeatingIdx !== -1 ? rest.substring(byDefeatingIdx + ' by defeating '.length).replace(/\.$/, '') : null;
    const ampIdx = beastsPart !== null ? mainPart.indexOf(' & ') : -1;
    const xpSilverPart = ampIdx !== -1 ? mainPart.substring(0, ampIdx) : mainPart;
    const itemsPart = ampIdx !== -1 ? mainPart.substring(ampIdx + 3) : null;
    const pooledMatch = xpSilverPart.match(/ \(pooled\)/);
    const restedMatch = xpSilverPart.match(/ \((\d+) rested\)/);
    const cleanStats = xpSilverPart.replace(/ \(pooled\)/, '').replace(/ \(\d+ rested\)/, '');
    return (
      <>
        <span className={styles.rewardName}>{name}</span>
        {' receives '}
        <span className={styles.rewardStats}>
          {cleanStats}
          {restedMatch && (
            <span className={styles.restedTag} data-tooltip={`+${restedMatch[1]} bonus XP from rested experience`} tabIndex={0}>{restedMatch[1]} rested</span>
          )}
          {pooledMatch && (
            <span className={styles.pooledTag} data-tooltip="XP stored in pool, paid out after the tournament ends" tabIndex={0}>pooled</span>
          )}
        </span>
        {itemsPart && (
          <>{' & '}{itemsPart.split(', ').map((item, i, arr) => (
            <React.Fragment key={i}>
              <span className={styles.rewardItem}>{item}</span>
              {i < arr.length - 1 ? ', ' : ''}
            </React.Fragment>
          ))}</>
        )}
        {beastsPart ? (
          <>{' by defeating '}<span className={styles.rewardBeasts}>{beastsPart}</span>{'.'}</>
        ) : '.'}
      </>
    );
  };

  const renderRewards = () => {
    const rewardsLog = battleReport?.reportData?.combatLog?.filter((log: string) => {
      const { eventType, text } = parseCombatTag(log);
      return isRewardLine(eventType, text);
    });
    if (!rewardsLog || rewardsLog.length === 0) return null;
    return (
      <div className={styles.rewardsSection}>
        <h4>Battle Rewards</h4>
        {rewardsLog.map((reward: string, index: number) => (
          <p key={index}>{parseRewardLine(reward)}</p>
        ))}
      </div>
    );
  };

  const renderSummaryTable = (teamType: string, teamSummary: any, participantSummaries: any) => {
    if (!participantSummaries || !teamSummary) return null;
    const anyTaunts = Object.values(participantSummaries).some((p: any) => (p.taunts ?? 0) > 0);
    return (
      <div className={styles.tableContainer}>
        <table className={styles.summaryTable}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Total Dmg</th>
              <th>Largest Hit</th>
              <th>Dmg Taken</th>
              <th>Attacks</th>
              <th>Dodges</th>
              <th>Parries</th>
              <th>Blocks</th>
              <th>Missed</th>
              <th>Critical</th>
              {anyTaunts && <th>Taunts</th>}
            </tr>
          </thead>
          <tbody>
            {Object.keys(participantSummaries).map((key) => {
              const participant = participantSummaries[key];
              if (participant.team !== teamType) return null;
              return (
                <tr key={key}>
                  <td>{participant.name}</td>
                  <td>{participant.damage}</td>
                  <td>{participant.highestDamageDealt}</td>
                  <td>{participant.damageTaken}</td>
                  <td>{participant.attacksMade}</td>
                  <td>{participant.dodgedAttacks}</td>
                  <td>{participant.parries}</td>
                  <td>{participant.blockedAttacks}</td>
                  <td>{participant.missedAttacks}</td>
                  <td>{participant.criticalHits}</td>
                  {anyTaunts && <td>{participant.taunts ?? 0}</td>}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td>Team Total</td>
              <td>{teamSummary.totalDamage}</td>
              <td>{teamSummary.highestDamageDealt}</td>
              <td>{teamSummary.damageTaken}</td>
              <td>{teamSummary.attacksMade}</td>
              <td>{teamSummary.dodgedAttacks}</td>
              <td>{teamSummary.parries}</td>
              <td>{teamSummary.blockedAttacks}</td>
              <td>{teamSummary.missedAttacks}</td>
              <td>{teamSummary.criticalHits}</td>
              {anyTaunts && <td>{teamSummary.taunts ?? 0}</td>}
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  const showSummary = settingsLoaded && battleLogMode !== 'new2d' && (battleLogMode === 'instant' || viewingComplete);
  const show2dSummary = settingsLoaded && battleLogMode === 'new2d' && viewingComplete;

  if (error) {
    return (
      <div className={styles.battleContainer}>
        <div className={styles.deathHeader}>
          <h1 className={styles.deathTitle}>Your Mercenary Has Fallen</h1>
          <p className={styles.deathSubtitle}>The battle report could not be retrieved.</p>
        </div>
        <div className={styles.createSection}>
          <p className={styles.createPrompt}>Your mercenary's story has ended. Begin anew.</p>
          <div className={styles.actionRow}>
            <button className={styles.createButton} onClick={handleCreateNewMercenary}>Create New Mercenary</button>
            <button className={styles.switchButton} onClick={handleSwitchMercenary}>Switch to Existing Mercenary</button>
          </div>
        </div>
      </div>
    );
  }

  if (!battleReport) {
    return (
      <div className={styles.battleContainer}>
        <p>Loading battle report...</p>
      </div>
    );
  }

  const showDeathBanner = showSummary || show2dSummary;

  return (
    <div className={`${styles.battleContainer}${showDeathBanner ? ' ' + styles.battleContainerDead : ''}`}>
      {/* Death Header — text modes only; 2D viewer shows its own victory screen */}
      {showDeathBanner && battleLogMode !== 'new2d' && (
        <div className={styles.deathHeader}>
          <h1 className={styles.deathTitle}>Your Mercenary Has Fallen</h1>
          <p className={styles.deathSubtitle}>
            {battleReport.reportData.winner === 'teamOne' ? 'Team One Wins!' : 'Team Two Wins!'}
          </p>
        </div>
      )}

      {/* Info Panel */}
      <div className={styles.infoPanel}>
        <h2 className={styles.battleTitle}>
          <span className={styles.battleTitleLine} />
          <span className={styles.battleTitleText}>{battleReport.title}</span>
          <span className={styles.battleTitleLine} />
        </h2>
        <p className={styles.battleMeta}>
          <span className={styles.battleMetaSeason}>{battleReport.reportData.seasonName}</span>
          <span className={styles.battleMetaSep}>—</span>
          <span>{formatDateTimeUTC(battleReport.createdAt)} UTC</span>
        </p>
        {battleLogMode !== 'new2d' && (
          <div className={styles.teamPreviews}>
            {renderTeamPreview('teamOne', battleReport.reportData.participantSummaries)}
            {renderTeamPreview('teamTwo', battleReport.reportData.participantSummaries)}
          </div>
        )}
      </div>

      {/* 2D Viewer */}
      {battleLogMode === 'new2d' && settingsLoaded && (() => {
        // Inject permanentlyDied for the player's mercenary so the viewer shows the death overlay.
        // This is needed for battles stored before the permanentlyDied flag was added to the backend.
        const battleDataWithDeath = (() => {
          const clone = JSON.parse(JSON.stringify(battleReport)) as BattleReportData;
          const playerKey = Object.keys(clone.reportData.participantSummaries).find(
            k => clone.reportData.participantSummaries[k].combatantId === playerMercenaryId
          );
          if (playerKey) {
            (clone.reportData.participantSummaries[playerKey] as any).permanentlyDied = true;
          }
          return clone;
        })();
        return (
          <BattleViewer2D
            initialBattleData={battleDataWithDeath as unknown as BattleData}
            onComplete={handleViewingComplete}
            myMercenaryId={playerMercenaryId || undefined}
          />
        );
      })()}

      {/* Scrolling mode */}
      {battleLogMode === 'scrolling' && !viewingComplete && (
        <>
          <div className={styles.combatLog}>
            {battleReport.reportData.combatLog
              .slice(0, revealedCount)
              .filter((log: string) => {
                const { eventType, text } = parseCombatTag(log);
                return !isRewardLine(eventType, text);
              })
              .map((log: string, index: number) => {
                const { eventType, text } = parseCombatTag(log);
                if (isRoundHeader(eventType, text)) {
                  return <div key={index} className={styles.roundHeader}>{text.replace(/---/g, '').trim()}</div>;
                }
                return <p key={index}>{parseLogWithColors(log)}</p>;
              })}
          </div>
          <div className={styles.actionRow}>
            <button className={styles.secondaryButton} onClick={skipScrolling}>Skip to Results</button>
          </div>
        </>
      )}

      {/* Instant mode */}
      {battleLogMode === 'instant' && (
        <div className={styles.combatLog}>
          {battleReport.reportData.combatLog
            .filter((log: string) => {
              const { eventType, text } = parseCombatTag(log);
              return !isRewardLine(eventType, text);
            })
            .map((log: string, index: number) => {
              const { eventType, text } = parseCombatTag(log);
              if (isRoundHeader(eventType, text)) {
                return <div key={index} className={styles.roundHeader}>{text.replace(/---/g, '').trim()}</div>;
              }
              return <p key={index}>{parseLogWithColors(log)}</p>;
            })}
        </div>
      )}

      {/* Summary & rewards (text modes) */}
      {showSummary && (
        <>
          <h3 className={styles.summaryHeading}>Battle Summary</h3>
          <div className={styles.summaryTables}>
            {renderSummaryTable('teamOne', battleReport.reportData.teamSummaries.teamOne, battleReport.reportData.participantSummaries)}
            {renderSummaryTable('teamTwo', battleReport.reportData.teamSummaries.teamTwo, battleReport.reportData.participantSummaries)}
          </div>
          {renderRewards()}
        </>
      )}

      {/* Summary & rewards (2D mode, shown after animation completes) */}
      {show2dSummary && (
        <>
          <h3 className={styles.summaryHeading}>Battle Summary</h3>
          <div className={styles.summaryTables}>
            {renderSummaryTable('teamOne', battleReport.reportData.teamSummaries.teamOne, battleReport.reportData.participantSummaries)}
            {renderSummaryTable('teamTwo', battleReport.reportData.teamSummaries.teamTwo, battleReport.reportData.participantSummaries)}
          </div>
          {renderRewards()}
        </>
      )}

      {/* Create / Switch — shown once the battle is done */}
      {(showSummary || show2dSummary) && (
        <div className={styles.createSection}>
          <p className={styles.createPrompt}>Your mercenary's story has ended. Begin anew.</p>
          <div className={styles.actionRow}>
            <button className={styles.createButton} onClick={handleCreateNewMercenary}>Create New Mercenary</button>
            <button className={styles.switchButton} onClick={handleSwitchMercenary}>Switch to Existing Mercenary</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeathScreen;

